import { toast } from 'react-toastify';

const LOADING_TOAST_ID = 'crm-status-loading';
const TOAST_AUTO_CLOSE_MS = 3200;

const baseOptions = {
  className: 'crm-toast',
  bodyClassName: 'crm-toast__body',
  progressClassName: 'crm-toast__progress',
};

/**
 * Show a status message as a toast. Used by StatusBanner and any direct callers.
 * Loading stays open until the next success/error/ready toast replaces it.
 */
export function showStatusToast(status) {
  if (!status?.message) {
    if (status?.type !== 'loading') {
      toast.dismiss(LOADING_TOAST_ID);
    }
    return;
  }

  const message = String(status.message);
  const type = status.type || 'info';

  if (type === 'loading') {
    // Always use a single sticky toast id — never stack loading popups during sync polls.
    if (toast.isActive(LOADING_TOAST_ID)) {
      toast.update(LOADING_TOAST_ID, {
        render: message,
        type: 'default',
        isLoading: true,
        autoClose: false,
        ...baseOptions,
        className: 'crm-toast crm-toast--loading',
      });
    } else {
      toast.dismiss(LOADING_TOAST_ID);
      toast(message, {
        toastId: LOADING_TOAST_ID,
        type: 'default',
        isLoading: true,
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        ...baseOptions,
        className: 'crm-toast crm-toast--loading',
      });
    }
    return;
  }

  // Only terminal statuses dismiss the sticky loading toast.
  toast.dismiss(LOADING_TOAST_ID);

  if (type === 'error') {
    toast.error(message, {
      toastId: `error:${message}`,
      autoClose: TOAST_AUTO_CLOSE_MS,
      ...baseOptions,
      className: 'crm-toast crm-toast--error',
    });
    return;
  }

  if (type === 'success') {
    toast.success(message, {
      toastId: `success:${message}`,
      autoClose: TOAST_AUTO_CLOSE_MS,
      ...baseOptions,
      className: 'crm-toast crm-toast--success',
    });
    return;
  }

  if (type === 'warning') {
    toast.warning(message, {
      toastId: `warning:${message}`,
      autoClose: TOAST_AUTO_CLOSE_MS,
      ...baseOptions,
      className: 'crm-toast crm-toast--warning',
    });
    return;
  }

  if (type === 'ready' || type === 'idle') {
    return;
  }

  toast.info(message, {
    toastId: `info:${message}`,
    autoClose: TOAST_AUTO_CLOSE_MS,
    ...baseOptions,
    className: 'crm-toast crm-toast--info',
  });
}
